import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers/yup';

import { subItemFilterSchema } from '../../../form-schemas/subitemFilterSchema';

const defaultValue = (t) => ({
  sum: {
    checked: true,
    column: null,
    label: 'Sum',
  },
  header: {
    checked: false,
    color: '#F5F6F8',
    rgb: { r: 245, g: 246, b: 248 },
  },
  tax: {
    checked: true,
    label: 'Tax',
    type: null,
    value: '',
  },
  currency: {
    checked: true,
    type: null,
    position: { value: 'before', label: t('Before the value') },
    column: [],
  },
  columns: [],
  columnWidth: null,
  totalSum: 0,
  filterConfig: {
    isFilterEnabled: false,
    filterChaining: '',
    filters: [],
  },
  itemNameColumn: {
    checked: true,
    label: 'Item',
  },
});
const useLineItemState = ({ selectedItem, triggerToast }) => {
  const tableHeaderRef = useRef(null);
  const { t } = useTranslation();

  const [formData, setFormData] = useState(defaultValue(t));

  const filterFormMethods = useForm({
    defaultValue:
      selectedItem?.subItemSettings?.filterConfig || formData.filterConfig,
    resolver: yupResolver(subItemFilterSchema),
  });

  const { append, remove, update } = useFieldArray({
    control: filterFormMethods.control,
    name: 'filters',
  });

  const taxTypeOptions = useMemo(
    () => [
      { value: 'percentage', label: `${t('Percentage')} (%)` },
      { value: 'fixed', label: t('Fixed') },
    ],
    [],
  );

  const currencyPositionOptions = useMemo(
    () => [
      { value: 'before', label: t('Before the value') },
      { value: 'after', label: t('After the value') },
    ],
    [],
  );

  const currencyTypesOptions = useMemo(
    () => [
      { value: 'usd', label: 'USD', symbol: '$' },
      { value: 'gbp', label: 'GBP', symbol: '£' },
      { value: 'eur', label: 'EUR', symbol: '€' },
      { value: 'aud', label: 'AUD', symbol: 'AUD' },
      { value: 'cad', label: 'CAD', symbol: 'CAD' },
      { value: 'zar', label: 'ZAR', symbol: 'R' },
      { value: 'ils', label: 'ILS', symbol: '₪' },
    ],
    [],
  );

  const rgbMapper = {
    '#040930': { r: 4, g: 9, b: 48 },
    '#396AFF': { r: 57, g: 106, b: 255 },
    '#0DB691': { r: 13, g: 182, b: 145 },
    '#FF8282': { r: 255, g: 130, b: 130 },
    '#647E9E': { r: 100, g: 126, b: 158 },
    '#FF2929': { r: 255, g: 41, b: 41 },
    '#A09C9C': { r: 160, g: 156, b: 156 },
    '#FFC107': { r: 255, g: 193, b: 7 },
    '#93A689': { r: 147, g: 166, b: 137 },
    '#F5F6F8': { r: 245, g: 246, b: 248 },
  };

  const updateNestedProperty = (obj, path, value) => {
    const parts = path.split('.');

    setFormData((prev) => {
      const updatedFormValues = JSON.parse(JSON.stringify(prev));
      let current = updatedFormValues;

      // Traverse the object, stopping before the last key
      for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        if (!current[key]) current[key] = {}; // Ensure the key exists
        current = current[key];
      }

      // Set the final property value
      current[parts[parts.length - 1]] = value;

      return { ...updatedFormValues };
    });
  };

  const handleColumns = (value) => {
    setFormData((prev) => {
      const updatedColumns = [...prev?.columns];
      const index =
        updatedColumns?.findIndex((obj) => obj?.id === value.id) || 0;
      if (index < 0) {
        updatedColumns.push(value);
      } else {
        updatedColumns.splice(index, 1);
      }
      return { ...prev, columns: updatedColumns };
    });
  };

  const handleColumnWidth = (value) => {
    setFormData((prev) => {
      return { ...prev, columnWidth: value };
    });
  };

  useEffect(() => {
    if (selectedItem?.subItemSettings) {
      setFormData({ ...defaultValue(t), ...selectedItem.subItemSettings });
    }
    if (selectedItem?.subItemSettings?.filterConfig) {
      filterFormMethods.reset(selectedItem?.subItemSettings?.filterConfig);
    }
  }, [selectedItem]);

  const updateFormData = useCallback(
    (path, newValue) => {
      if (['columns'].includes(path)) {
        handleColumns(newValue);
        return;
      } else if (['columnWidth'].includes(path)) {
        handleColumnWidth(newValue);
        return;
      } else {
        updateNestedProperty(formData, path, newValue);
      }
    },
    [setFormData],
  );

  const clearFormData = () => {
    setFormData(defaultValue(t));
  };

  const validateFormData = () => {
    // validate sum
    if (formData.sum.checked && !formData.sum.column) {
      triggerToast(t('Please set column to sum to proceed.'));
      return false;
    }
    // validate header color
    if (formData.header.checked && !formData.header.color) {
      triggerToast(t('Please select color of your choice for the header.'));
      return false;
    }
    // validate tax
    if (
      (formData.tax.checked && !formData.tax.type) ||
      (formData.tax.checked && !formData.tax.value)
    ) {
      triggerToast(t('Please fill the tax related fields for calculation.'));
      return false;
    }
    // validate currency
    if (
      (formData.currency.checked && !formData.currency.type) ||
      (formData.currency.checked && !formData.currency.position)
    ) {
      triggerToast(t('Please fill the currency related fields to proceed.'));
      return false;
    }
    // validate columns
    if (formData.columns.length > 6) {
      triggerToast(t('You cannot add more than six columns.'));
      return false;
    }
    return true;
  };

  const updateFilters = (action, payload) => {
    switch (action) {
      case 'add':
        append(payload);
        break;
      case 'remove':
        remove(payload);
        break;
      case 'update':
        const { index, filter } = payload;
        update(index, filter);
        break;
      default:
        break;
    }
  };

  return {
    formData,
    rgbMapper,
    taxTypeOptions,
    currencyTypesOptions,
    currencyPositionOptions,
    tableHeaderRef,
    clearFormData,
    updateFormData,
    validateFormData,
    updateFilters,
    filterFormMethods,
  };
};

export default useLineItemState;
